/**
 * Dialog di composizione: nuovo messaggio, risposta (prefill Re: + quote +
 * threading) e inoltro (prefill Fwd:, il backend riallega gli allegati
 * originali). Allegati in upload -> endpoint multipart (validati dal
 * backend con attachment_security). To/CC/BCC separati da virgola; il
 * lookup contatti searchable dall'anagrafica arriva con l'aggancio al
 * modulo clienti (gap nel ledger).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@floraltech/ui';
import { emailFetch } from '../lib/api';

function parseIndirizzi(testo: string): string[] {
  return testo
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Prefill del compose per risposta/inoltro. */
export interface ComposePrefill {
  to?: string;
  oggetto?: string;
  corpo?: string;
  rispondi_a_messaggio_id?: string;
  inoltra_da_messaggio_id?: string;
}

interface Props {
  accountId: string;
  open: boolean;
  prefill?: ComposePrefill | null;
  onOpenChange: (open: boolean) => void;
  onInviato: () => void;
}

export function ComposeDialog({ accountId, open, prefill, onOpenChange, onInviato }: Props) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [oggetto, setOggetto] = useState('');
  const [corpo, setCorpo] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTo(prefill?.to ?? '');
      setCc('');
      setBcc('');
      setOggetto(prefill?.oggetto ?? '');
      setCorpo(prefill?.corpo ?? '');
      setFiles([]);
      setErrore(null);
    }
  }, [open, prefill]);

  const invia = async () => {
    setInvio(true);
    setErrore(null);
    const dati = {
      account_id: accountId,
      to: parseIndirizzi(to),
      cc: parseIndirizzi(cc),
      bcc: parseIndirizzi(bcc),
      oggetto,
      corpo,
      rispondi_a_messaggio_id: prefill?.rispondi_a_messaggio_id ?? null,
      inoltra_da_messaggio_id: prefill?.inoltra_da_messaggio_id ?? null,
    };
    try {
      let res: Response;
      if (files.length > 0) {
        const form = new FormData();
        form.append('dati', JSON.stringify(dati));
        for (const f of files) form.append('files', f);
        const token = localStorage.getItem('token');
        res = await fetch('/api/v1/email/compose/invia-multipart', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
      } else {
        res = await emailFetch('/compose/invia', {
          method: 'POST',
          body: JSON.stringify(dati),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setErrore(body?.detail ?? `Errore ${res.status}`);
        return;
      }
      onOpenChange(false);
      onInviato();
    } catch {
      setErrore('Errore di rete');
    } finally {
      setInvio(false);
    }
  };

  const titolo = prefill?.rispondi_a_messaggio_id
    ? 'Rispondi'
    : prefill?.inoltra_da_messaggio_id
      ? 'Inoltra'
      : 'Nuovo messaggio';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{titolo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="compose-to">A (separati da virgola)</Label>
            <Input
              id="compose-to"
              value={to}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
              placeholder="destinatario@esempio.it"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="compose-cc">Cc</Label>
              <Input
                id="compose-cc"
                value={cc}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCc(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="compose-bcc">Ccn</Label>
              <Input
                id="compose-bcc"
                value={bcc}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBcc(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="compose-oggetto">Oggetto</Label>
            <Input
              id="compose-oggetto"
              value={oggetto}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOggetto(e.target.value)
              }
            />
          </div>
          <div>
            <Label htmlFor="compose-corpo">Messaggio</Label>
            <textarea
              id="compose-corpo"
              className="min-h-[180px] w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInput.current?.click()}
            >
              <Paperclip className="mr-1 h-4 w-4" />
              Allega
            </Button>
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const nuovi = Array.from(e.target.files ?? []);
                setFiles((f) => [...f, ...nuovi]);
                e.target.value = '';
              }}
            />
            {files.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs"
              >
                {f.name}
                <button
                  type="button"
                  aria-label={`Rimuovi ${f.name}`}
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {prefill?.inoltra_da_messaggio_id && (
              <span className="text-xs text-muted-foreground">
                Gli allegati del messaggio originale vengono riallegati automaticamente.
              </span>
            )}
          </div>

          {errore && <p className="text-sm text-destructive">{errore}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={invio}>
            Annulla
          </Button>
          <Button onClick={invia} disabled={invio || parseIndirizzi(to).length === 0}>
            {invio ? 'Invio...' : 'Invia'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
